import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Select, Typography, Space, Tooltip, Badge } from 'antd';
import { GlobalOutlined, TeamOutlined, SearchOutlined, ReloadOutlined, BookOutlined } from '@ant-design/icons';
import { useTenant } from '../../context/TenantContext';
import { isM365Managed } from '../../utils/customerKey';

const { Text } = Typography;

const SelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

// "Customer:" as an Inter kicker
const SelectorLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 10.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;

  ${({ theme }) => theme.mediaQuery.maxSm} {
    display: none;
  }
`;

const StyledSelect = styled(Select)`
  min-width: 240px;

  /* Layout only: background, edge and focus ring come from the Select tokens */
  .ant-select-selector {
    border-radius: 10px !important;
    height: 40px !important;
    padding: 0 12px !important;
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  .ant-select-selection-item {
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
    line-height: 38px !important;
  }

  ${({ theme }) => theme.mediaQuery.maxSm} {
    /* Phone-width header overflow fix: the 180px floor above (already a reduced
       value from the 240px desktop min-width) still doesn't fit inside
       DashboardHeader's non-wrapping flex row at <=640px alongside the other
       header controls — the header row's fixed-width floors sum to ~500px of
       chrome against a 375px viewport, so this must shrink at phone widths.
       Drop the hard floor and give it a fixed, compact width instead. */
    min-width: 0;
    width: 84px;
  }
`;

const TenantOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 4px 0;
`;

const TenantInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const TenantName = styled.span`
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TenantDomain = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TenantCount = styled(Badge)`
  .ant-badge-count {
    font-size: 10px;
    min-width: 18px;
    height: 18px;
    line-height: 18px;
  }

  ${({ theme }) => theme.mediaQuery.maxSm} {
    /* Decorative tenant-count badge; drop it at phone widths to free up
       header row space for the (now narrower) selector itself. */
    display: none;
  }
`;

const AllTenantsOption = styled(TenantOption)`
  color: ${({ theme }) => theme.colors.accent};
`;

const KnowledgeBaseOption = styled(TenantOption)`
  color: var(--odin-warning);
`;

const SelectedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * TenantSelector - Dropdown component for selecting customer tenants
 *
 * Wired to CIPP-MCP data via TenantContext:
 * - Displays tenant displayName from CIPP
 * - Stores tenantId as selection value (mapped to 'id' in context)
 * - Provides search filtering by name and domain
 * - Shows tenant count and default domain info
 */
const TenantSelector = () => {
  const {
    selectedTenant,
    tenants,
    isLoading,
    error,
    selectTenant,
    clearSelection,
    enterKnowledgeMode,
    knowledgeBaseMode,
  } = useTenant();

  const [searchValue, setSearchValue] = useState('');

  /**
   * Handle tenant selection change
   * @param {string} value - Either 'all' or tenant id (maps to CIPP tenantId)
   */
  const handleChange = useCallback((value) => {
    setSearchValue('');
    if (value === '__knowledge__') {
      enterKnowledgeMode();
    } else if (value === 'all') {
      clearSelection();
    } else {
      // value is tenant.id which maps to CIPP's tenantId
      selectTenant(value);
    }
  }, [selectTenant, clearSelection, enterKnowledgeMode]);

  /**
   * Handle search input changes
   */
  const handleSearch = useCallback((value) => {
    setSearchValue(value);
  }, []);

  /**
   * Filter function for search
   * Searches by displayName and defaultDomain
   */
  const filterOption = useCallback((input, option) => {
    if (!input) return true;

    // Handle the "All Customers" option
    if (option?.value === 'all') {
      return 'all customers'.includes(input.toLowerCase());
    }

    // Handle the "Knowledge Base" option
    if (option?.value === '__knowledge__') {
      return 'knowledge base documentation search'.includes(input.toLowerCase());
    }

    // Get search data from option
    const searchData = option?.['data-search'];
    if (searchData) {
      return searchData.toLowerCase().includes(input.toLowerCase());
    }

    return false;
  }, []);

  /**
   * Build dropdown options from tenant context
   * Uses displayName from CIPP and stores id (tenantId) as value
   * Sorted alphabetically (A-Z) by displayName
   */
  const options = useMemo(() => {
    // Sort tenants alphabetically by displayName (case-insensitive)
    const sortedTenants = [...tenants].sort((a, b) => {
      const nameA = (a.displayName || '').toLowerCase();
      const nameB = (b.displayName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const tenantOptions = sortedTenants.map(tenant => ({
      value: tenant.id, // This is CIPP's tenantId, mapped in useTenantFetch
      label: (
        <TenantOption>
          <TeamOutlined style={{ color: 'var(--odin-accent)', flexShrink: 0 }} />
          <TenantInfo>
            <TenantName>{tenant.displayName}</TenantName>
            {tenant.defaultDomain && tenant.defaultDomain !== 'N/A' && (
              <TenantDomain>{tenant.defaultDomain}</TenantDomain>
            )}
            {/* No CIPP id: CSG doesn't manage this customer's M365 (spec 2026-10-19) */}
            {!isM365Managed(tenant) && <TenantDomain>M365 not managed</TenantDomain>}
          </TenantInfo>
        </TenantOption>
      ),
      // Store searchable data for filtering
      'data-search': `${tenant.displayName} ${tenant.defaultDomain || ''}`,
    }));

    // Add "All Customers" and "Knowledge Base" options at the top
    return [
      {
        value: 'all',
        label: (
          <AllTenantsOption>
            <GlobalOutlined style={{ flexShrink: 0 }} />
            <TenantInfo>
              <TenantName>All Customers</TenantName>
              {tenants.length > 0 && (
                <TenantDomain>{tenants.length} tenant{tenants.length !== 1 ? 's' : ''} available</TenantDomain>
              )}
            </TenantInfo>
          </AllTenantsOption>
        ),
        'data-search': 'all customers',
      },
      {
        value: '__knowledge__',
        label: (
          <KnowledgeBaseOption>
            <BookOutlined style={{ flexShrink: 0 }} />
            <TenantInfo>
              <TenantName>Knowledge Base</TenantName>
              <TenantDomain>Search documentation &amp; internal knowledge</TenantDomain>
            </TenantInfo>
          </KnowledgeBaseOption>
        ),
        'data-search': 'knowledge base documentation search',
      },
      ...tenantOptions,
    ];
  }, [tenants]);

  /**
   * Get the display value for the selector
   */
  const displayValue = useMemo(() => {
    if (knowledgeBaseMode) return '__knowledge__';
    if (!selectedTenant) return 'all';
    return selectedTenant.id; // id maps to CIPP's tenantId
  }, [selectedTenant, knowledgeBaseMode]);

  /**
   * Custom label render for clean display in the selector box
   * Shows a simple text label instead of the complex dropdown option component
   */
  const labelRender = useCallback(({ value }) => {
    if (value === 'all') {
      return (
        <SelectedLabel>
          <GlobalOutlined style={{ color: 'var(--odin-accent)', flexShrink: 0 }} />
          <span>All Customers</span>
        </SelectedLabel>
      );
    }
    if (value === '__knowledge__') {
      return (
        <SelectedLabel>
          <BookOutlined style={{ color: 'var(--odin-warning)', flexShrink: 0 }} />
          <span>Knowledge Base</span>
        </SelectedLabel>
      );
    }
    // Find the tenant by id
    const tenant = tenants.find(t => t.id === value);
    if (tenant) {
      return (
        <SelectedLabel>
          <TeamOutlined style={{ color: 'var(--odin-accent)', flexShrink: 0 }} />
          <span>{tenant.displayName}</span>
        </SelectedLabel>
      );
    }
    return <span>{value}</span>;
  }, [tenants]);

  /**
   * Dropdown render for custom styling
   */
  const dropdownRender = useCallback((menu) => (
    <div>
      {error && (
        <div style={{ padding: '8px 12px', color: 'var(--odin-error)', borderBottom: '1px solid var(--odin-border)' }}>
          Error loading tenants: {error}
        </div>
      )}
      {menu}
    </div>
  ), [error]);

  return (
    <SelectorContainer>
      <SelectorLabel>
        Customer:
      </SelectorLabel>
      <Tooltip
        title={knowledgeBaseMode ? 'Knowledge Base mode' : selectedTenant ? `Selected: ${selectedTenant.displayName}` : 'Viewing all customers'}
        placement="bottom"
      >
        <StyledSelect
          value={displayValue}
          onChange={handleChange}
          options={options}
          loading={isLoading}
          disabled={isLoading && tenants.length === 0}
          placeholder="Select customer..."
          showSearch
          searchValue={searchValue}
          onSearch={handleSearch}
          filterOption={filterOption}
          optionFilterProp="children"
          labelRender={labelRender}
          notFoundContent={
            isLoading ? 'Loading tenants...' :
            error ? 'Error loading tenants' :
            'No tenants found'
          }
          dropdownRender={dropdownRender}
          dropdownStyle={{
            minWidth: 280,
            maxHeight: 400,
          }}
          listHeight={350}
          virtual={tenants.length > 50}
          suffixIcon={
            isLoading ? <ReloadOutlined spin /> : <SearchOutlined />
          }
        />
      </Tooltip>
      {tenants.length > 0 && (
        <TenantCount count={tenants.length} title={`${tenants.length} total tenants`} />
      )}
    </SelectorContainer>
  );
};

export default TenantSelector;
